// src/App.jsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Download } from 'lucide-react';
import FileUpload from '@components/upload/FileUpload';
import Alert from '@components/Alert';
import ResultsDisplay from '@components/results/ResultsDisplay';
import DarkModeToggle from '@components/DarkModeToggle';
import ProgressIndicator from '@components/ProgressIndicator';
import FAQ from '@components/FAQ';
import { useAnalysis } from '@hooks/useAnalysis';
import { useFileUpload } from '@hooks/useFileUpload';
import { exportAnalysisToPDF } from '@utils/exportToPDF';

export default function App() {
  // ---- file upload / parsing ----
  const {
    isLoading: isUploading,
    error: fileError,
    hasFile,
    parsedContent,
    uploadFile,
  } = useFileUpload();

  // ---- analysis ----
  const {
    results,
    isAnalyzing,
    error: analysisError,
    analyzeResume,
    clearResults,
  } = useAnalysis();

  // ---- UI state ----
  const [jobDescription, setJobDescription] = useState('');
  const [uiError, setUiError] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // handlers
  const handleUpload = async (file) => {
    setUiError(null);
    setShowResults(false);
    clearResults();
    await uploadFile(file);
  };

  const handleAnalyze = async (e) => {
    e?.preventDefault();
    
    console.log('🔍 ===== ANALYZE BUTTON CLICKED =====');
    console.log('📄 Resume parsed?', !!parsedContent);
    console.log('📄 Resume text length:', parsedContent?.text?.length || 0);
    console.log('📝 Job description length:', jobDescription?.length || 0);
    
    setUiError(null);
    setShowResults(false);

    const resumeText = parsedContent?.text || '';

    if (!resumeText || resumeText.trim().length < 50) {
      console.log('❌ ERROR: Resume too short or missing');
      const errorMsg = 'Please upload a readable resume first (≥ 50 characters).';
      console.log('Setting error:', errorMsg);
      setUiError(errorMsg);
      return;
    }
    
    if (!jobDescription || jobDescription.trim().length < 50) {
      console.log('❌ ERROR: Job description too short');
      const errorMsg = 'Please paste a fuller job description (≥ 50 characters).';
      console.log('Setting error:', errorMsg);
      setUiError(errorMsg);
      return;
    }

    console.log('✅ Validation passed! Starting analysis...');
    console.log('Calling analyzeResume with:', {
      resumeLength: resumeText.length,
      jobDescLength: jobDescription.length
    });
    
    try {
      const r = await analyzeResume(resumeText, jobDescription);
      console.log('📊 Analysis result:', r);
      if (r) {
        console.log('✅ Setting showResults to true');
        setShowResults(true);
      }
    } catch (error) {
      console.error('💥 Analysis error:', error);
    }
  };

  const handleExportPDF = async () => {
    if (!results) return;
    
    setIsExporting(true);
    try {
      const result = await exportAnalysisToPDF(results, parsedContent?.name, 'Position');
      if (!result.success) {
        setUiError('Failed to export PDF. Please try again.');
      }
    } catch (error) {
      setUiError('Failed to export PDF. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
      {/* Top nav */}
      <header className="sticky top-0 z-10 bg-white/80 dark:bg-gray-800/80 backdrop-blur border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-cyan-100 dark:bg-cyan-900 flex items-center justify-center">
              <span className="text-cyan-600 dark:text-cyan-400 font-semibold">SR</span>
            </div>
            <div className="text-lg font-semibold">
              <span className="text-gray-800 dark:text-gray-100">Smart</span>
              <span className="text-cyan-600 dark:text-cyan-400">ResumeTool</span>
            </div>
            <span className="ml-2 text-xs px-2 py-1 rounded-full bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
              Powered by AI
            </span>
          </div>
          <nav className="flex items-center gap-4">
            <div className="hidden sm:flex items-center gap-6 text-sm text-gray-600 dark:text-gray-400">
              <Link to="/" className="hover:text-gray-900 dark:hover:text-gray-100">Home</Link>
              <Link to="/about" className="hover:text-gray-900 dark:hover:text-gray-100">About</Link>
            </div>
            <DarkModeToggle />
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 pt-10 pb-6">
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-gray-900 dark:text-gray-100 text-center">
          Get Your Resume <span className="bg-gradient-to-r from-cyan-500 to-emerald-500 bg-clip-text text-transparent">ATS-Ready</span>
        </h1>

        <p className="mt-4 text-center text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
          Our AI-powered analyzer helps you optimize your resume for Applicant Tracking Systems and land more interviews.
        </p>

        {/* tiny benefits row */}
        <div className="mt-4 flex flex-wrap items-center justify-center gap-4 text-sm text-gray-700 dark:text-gray-300">
          <span className="flex items-center gap-2">
            <span>✅</span> Instant AI Analysis
          </span>
          <span className="flex items-center gap-2">
            <span>✅</span> AI-Generated Improvements
          </span>
          <span className="flex items-center gap-2">
            <span>✅</span> Smart Keyword Matching
          </span>
        </div>
      </section>

      {/* Main cards */}
      <main className="max-w-6xl mx-auto px-4 pb-12">
        {(uiError || fileError || analysisError) && (
          <div className="mb-4">
            <Alert variant="error">
              {uiError || fileError || analysisError}
            </Alert>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-8">
          {/* Upload card */}
          <section className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Upload Resume</h2>
              <span className="text-xs text-gray-500 dark:text-gray-400">Step 1 of 2</span>
            </div>

            <FileUpload
              onFileUpload={handleUpload}
              isLoading={isUploading || isAnalyzing}
              error={null}
            />

            {hasFile && parsedContent?.name && (
              <p className="mt-3 text-xs text-gray-600 dark:text-gray-400">
                Selected: <strong>{parsedContent.name}</strong>
                {parsedContent?.text?.length
                  ? <span> • {parsedContent.text.length.toLocaleString()} chars parsed</span>
                  : null}
              </p>
            )}
          </section>

          {/* JD card */}
          <section className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Job Description</h2>
              <span className="text-xs text-gray-500 dark:text-gray-400">Step 2 of 2</span>
            </div>

            <textarea
              className="w-full h-64 border border-gray-300 dark:border-gray-600 rounded-lg p-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
              placeholder="Paste the job description here…"
              value={jobDescription}
              onChange={(e) => setJobDescription(e.target.value)}
            />

            <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              💡 Pro tip: Include the entire job posting for best results.
              <span className="float-right">{jobDescription.length.toLocaleString()} chars</span>
            </div>
          </section>
        </div>

        {/* CTA */}
        <div className="mt-8 flex justify-center">
          <button
            type="button"
            onClick={handleAnalyze}
            disabled={isUploading || isAnalyzing}
            className="px-6 py-3 rounded-xl bg-gradient-to-r from-cyan-600 to-emerald-600 text-white font-semibold shadow-md hover:shadow-lg transition disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isAnalyzing ? 'Analyzing…' : 'Analyze My Resume'}
          </button>
        </div>

        {/* Progress Indicator */}
        {isAnalyzing && <ProgressIndicator isActive={isAnalyzing} />}

        {/* Results */}
        {showResults && results && (
          <section className="mt-10">
            {/* Export button */}
            <div className="flex justify-end mb-4">
              <button
                onClick={handleExportPDF}
                disabled={isExporting}
                className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 dark:bg-cyan-700 text-white rounded-lg hover:bg-cyan-700 dark:hover:bg-cyan-600 transition disabled:opacity-60"
              >
                <Download className="w-4 h-4" />
                {isExporting ? 'Exporting...' : 'Export Report'}
              </button>
            </div>
            
            <ResultsDisplay 
              results={results} 
              resumeName={parsedContent?.name}
              jobTitle="Position"
            />
          </section>
        )}

        {/* FAQ Section */}
        {!showResults && <FAQ />}
      </main>
    </div>
  );
}